import { Section, textVariants } from '@/components/atoms';
import APP_CONFIG from '@/config/app';
import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MarkdownRendererProps } from './interfaces/MarkdownRenderer';
interface SectionNode {
  level: number;
  title: string;
  content: string;
  children: SectionNode[];
}
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  markdown,
  className = '',
}) => {
  const { MAIN_GAP } = APP_CONFIG.STYLES;
  const spacingConfig = {
    gap: `gap${MAIN_GAP}`,
    marginBottom: `mb${MAIN_GAP}`,
  };
  const sectionTree = useMemo(() => {
    if (!markdown) return [];
    const blocks = markdown.split(/^(?=#+ )/m);
    const flatNodes: SectionNode[] = blocks
      .filter((block) => block.trim())
      .map((block) => {
        const lines = block.trim().split('\n');
        const titleLine = lines[0] || '';
        const match = titleLine.match(/^(#+)\s+(.*)/);
        if (match) {
          return {
            level: match[1].length,
            title: match[2].trim().replace(/^\*+|\*+$/g, ''),
            content: lines.slice(1).join('\n'),
            children: [],
          };
        } else {
          return {
            level: 0,
            title: '',
            content: block,
            children: [],
          };
        }
      });
    const root: SectionNode = {
      level: 0,
      title: '',
      content: '',
      children: [],
    };
    const stack = [root];
    for (const node of flatNodes) {
      let parent = stack[stack.length - 1];
      while (parent.level >= node.level) {
        stack.pop();
        parent = stack[stack.length - 1];
      }
      parent.children.push(node);
      stack.push(node);
    }
    return root.children;
  }, [markdown]);
  const renderNode = (
    node: SectionNode,
    isTopLevel = false,
  ): React.ReactNode => {
    const trimmedContent = node.content.trim();
    const bodyContent = trimmedContent ? (
      <div
        className={textVariants({
          size: 'body',
          color: 'secondary',
          className: '[&>:last-child]:mb-0',
        })}
        key="content"
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: () => null,
            h2: () => null,
            h3: () => null,
            h4: () => null,
            h5: () => null,
            h6: () => null,
            p: ({ node, ...props }) => (
              <p className={spacingConfig.marginBottom} {...props} />
            ),
            ul: ({ node, ...props }) => (
              <ul
                className={`list-disc list-inside ${spacingConfig.marginBottom}`}
                {...props}
              />
            ),
            ol: ({ node, ...props }) => (
              <ol
                className={`list-decimal list-inside ${spacingConfig.marginBottom}`}
                {...props}
              />
            ),
            li: ({ node, ...props }) => <li {...props} />,
            a: ({ node, ...props }) => (
              <a className={textVariants({ intent: 'link' })} {...props} />
            ),
            code: ({ node, inline, ...props }) =>
              inline ? (
                <code
                  className="bg-gray-700 text-red-400 px-1 py-0.5 rounded"
                  {...props}
                />
              ) : (
                <pre
                  className="bg-gray-800 text-white p-4 rounded-md overflow-x-auto"
                  {...props}
                />
              ),
          }}
        >
          {trimmedContent}
        </ReactMarkdown>
      </div>
    ) : null;
    const childSections = node.children.map((child) =>
      renderNode(child, false),
    );
    if (!node.title && !trimmedContent && node.children.length === 0) {
      return null;
    }
    const bodyElements = [bodyContent, ...childSections].filter(Boolean);
    return (
      <Section
        isBackgroundTransparent={!isTopLevel}
        isVerticalOnlyBleed={!isTopLevel}
        headerTitle={node.title}
        body={
          bodyElements.length > 0 ? (
            <div className={`flex flex-col ${spacingConfig.gap}`}>
              {bodyElements}
            </div>
          ) : (
            []
          )
        }
      />
    );
  };
  return (
    <div className={`flex flex-col gap${MAIN_GAP} ${className}`}>
      {sectionTree.map((node, index) => (
        <div key={index}>{renderNode(node, true)}</div>
      ))}
    </div>
  );
};
export default MarkdownRenderer;
